import React, { useEffect, useRef, useState } from "react";
import useEscapeLayer from "./useEscape";

/*
 * MERGE… from the Leads bulk bar: the selected rows are one human, keep one.
 *
 * The record's confirm grammar (record/ConfirmCard): the question as the title,
 * the choice, the consequence on the evidence grey, Cancel and a black button.
 * The choice is which row stays; it opens on the server's pick (the row with an
 * email, then the richer timeline). Names that differ get a line saying so,
 * since nothing else stops two different people being folded.
 *
 * Props:
 *   preview   { rows: [{ id, name, email, job_title, company, company_stage, activities }], keep }
 *   run       (keepId) => Promise, the merge itself
 *   onCancel  () => void
 */
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// What the merge does, in the words of the rows on screen: an address is named
// only when a folded row has one, and as the primary when the kept row has none.
function consequence(rows, kept, acrossCompanies) {
  if (!kept) return "";
  const others = rows.filter((r) => r.id !== kept.id);
  const one = others.length === 1;
  const parts = [`${kept.name || "The kept record"} stays. ${one ? "The other record folds" : "The others fold"} into it: emails, calls and lists move over`];
  const addresses = others.filter((r) => r.email).map((r) => r.email);
  if (addresses.length && !kept.email) parts.push(`${addresses[0]} becomes its email`);
  else if (addresses.length) parts.push(`${addresses.join(", ")} ${addresses.length === 1 ? "stays as an alias" : "stay as aliases"}`);
  let s = `${parts.join(", ")}, and blank fields fill in.`;
  if (acrossCompanies) s += " A company left with nobody on it folds into the kept one when it is the same company.";
  return s;
}

export default function MergeCard({ preview, run, onCancel }) {
  const [keep, setKeep] = useState(preview.keep);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const ref = useRef(null);
  useEscapeLayer(true, () => !busy && onCancel());
  useEffect(() => {
    if (ref.current) ref.current.focus({ preventScroll: true });
  }, []);

  const rows = preview.rows;
  const names = new Set(rows.map((r) => (r.name || "").trim().toLowerCase()));
  const sameName = names.size === 1 && rows[0].name;
  const title = sameName ? `Merge ${rows.length} records of ${rows[0].name}?` : `Merge ${rows.length} records into one?`;
  const kept = rows.find((r) => r.id === keep);
  const companies = new Set(rows.map((r) => r.company_id).filter(Boolean));

  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await run(keep);
    } catch (e) {
      setError((e && e.message) || "That didn’t merge. Try again.");
      setBusy(false);
    }
  };

  return (
    <div className="rec-confirm rec-cc" role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} ref={ref}>
      <div className="rec-confirm__title">{title}</div>
      {!sameName && <div className="rec-cc__why">The names differ. Merge only if these are one person.</div>}

      <div className="rec-confirm__field" role="radiogroup" aria-label="Record to keep">
        <span className="rec-label">Keep</span>
        {rows.map((r) => (
          <label key={r.id} className={`rec-merge__opt${keep === r.id ? " is-on" : ""}`}>
            <input type="radio" name="merge-keep" checked={keep === r.id} disabled={busy} onChange={() => setKeep(r.id)} />
            <span className="rec-merge__body">
              <span className="rec-merge__name">{r.name || "No name"}{r.company ? ` · ${r.company}` : ""}</span>
              <span className="rec-merge__meta">
                {[r.email || "no email", r.job_title, plural(r.activities, "activity", "activities")].filter(Boolean).join(" · ")}
              </span>
            </span>
          </label>
        ))}
      </div>

      <div className="rec-consequence">{consequence(rows, kept, companies.size > 1)}</div>

      <div className="rec-confirm__actions">
        {error && <span className="rec-err">{error}</span>}
        <button type="button" className="rec-btn rec-btn--plain rec-cc__btn" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="rec-btn rec-btn--dark rec-cc__btn" disabled={busy || !keep} onClick={confirm}>
          {busy ? "Merging…" : "Merge"}
        </button>
      </div>
    </div>
  );
}
